import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Tooltip } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import {
  fetchVercelStatus,
  IN_FLIGHT_STATES,
  type VercelDeployment,
} from '../services/vercelStatusService';

// Compact live deploy-status pills for the global header (non-prod only): one
// for the frontend (admin app) and one for the backend (API); website deploys
// are excluded. Reads prod deploy state (single source of truth) and shares the
// ['vercel-status'] query with the /dev page panel, so they dedupe into one
// request.

const STATE_LABEL: Record<string, string> = {
  ready: 'Ready',
  building: 'Building',
  queued: 'Queued',
  error: 'Error',
  canceled: 'Canceled',
  skipped: 'Skipped',
  blocked: 'Blocked',
  rolled_back: 'Rolled back',
  cleaned_up: 'Cleaned up',
  unknown: 'Unknown',
};

// GitHub-ish status colors — readable as a small dot on the white header pill.
const STATE_DOT: Record<string, string> = {
  ready: '#3fb950',
  building: '#d29922',
  queued: '#d29922',
  error: '#f85149',
  canceled: '#d29922',
  skipped: '#8b949e',
  blocked: '#d29922',
  rolled_back: '#d29922',
  cleaned_up: '#8b949e',
  unknown: '#8b949e',
};

const relTime = (iso: string | null): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const isBackend = (project: string | null): boolean => /backend/i.test(project || '');
const isWebsite = (project: string | null): boolean => /website/i.test(project || '');

// Pick the deployment to represent a project group: whatever's in flight first
// (most interesting right now), then the latest non-cleanup deploy (cleanup is
// housekeeping, not a build status), then anything as a last resort. `deps` is
// already sorted newest-first by the API.
const pickHeadline = (deps: VercelDeployment[]): VercelDeployment | null => {
  if (deps.length === 0) return null;
  const inFlight = deps.find((d) => IN_FLIGHT_STATES.has(d.state));
  if (inFlight) return inFlight;
  return deps.find((d) => d.state !== 'cleaned_up') || deps[0];
};

const DeployPill: React.FC<{ tag: string; dep: VercelDeployment | null }> = ({ tag, dep }) => {
  const navigate = useNavigate();
  if (!dep) return null;

  const building = IN_FLIGHT_STATES.has(dep.state);
  const label = STATE_LABEL[dep.state] || dep.state;
  const env = dep.custom_environment || dep.target || 'preview';
  const tip =
    `${dep.project || tag} · ${env} · ${label}` +
    `${dep.sha ? ' · ' + dep.sha : ''}` +
    `${dep.updated_at ? ' · ' + relTime(dep.updated_at) : ''}` +
    ' — click for details';

  return (
    <Tooltip content={tip} relationship="label">
      <button
        type="button"
        className="deploy-status-badge"
        onClick={() => navigate('/dev')}
        aria-label={`${tag} deployment: ${label}`}
      >
        <span className="deploy-status-tag">{tag}</span>
        {building ? (
          <Spinner size="extra-tiny" />
        ) : (
          <span
            className="deploy-status-dot"
            style={{ backgroundColor: STATE_DOT[dep.state] || '#8b949e' }}
          />
        )}
        <span className="deploy-status-label">{label}</span>
      </button>
    </Tooltip>
  );
};

const HeaderDeployBadge: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['vercel-status'],
    queryFn: fetchVercelStatus,
    staleTime: 0,
    // Fast while a build is in flight; a light idle poll so the header still
    // notices a new deploy starting. Paused automatically on hidden tabs.
    refetchInterval: (query) => {
      const deps = query.state.data as VercelDeployment[] | undefined;
      return deps?.some((d) => IN_FLIGHT_STATES.has(d.state)) ? 5000 : 60000;
    },
  });

  const deps = data || [];
  if (deps.length === 0) return null;

  // Two badges: frontend (admin app) and backend (API). Website deploys are
  // intentionally excluded from the header.
  const fe = pickHeadline(deps.filter((d) => !isBackend(d.project) && !isWebsite(d.project)));
  const be = pickHeadline(deps.filter((d) => isBackend(d.project)));
  if (!fe && !be) return null;

  return (
    <span className="deploy-status-group">
      <DeployPill tag="FE" dep={fe} />
      <DeployPill tag="BE" dep={be} />
    </span>
  );
};

export default HeaderDeployBadge;
